import { Controller } from "@hotwired/stimulus"

// Shows the "Effect options" sub-controls that apply to the chosen text effect
// and hides (and disables) the rest — so a single reveal dropdown can drive the
// fill-only, accent-only and highlight-only controls without leaking stale
// values on submit.
//
// Which controls each preset uses is data-driven: the wrapper carries a
// `data-text-effect-options-param-map-value` JSON map ({ preset: [param, ...] })
// mirrored from Common::TextFxCatalog. The controller reads the current preset's
// param list and shows a block when the preset consumes one of the block's
// params. If the map is absent it falls back to the original hard-coded sets so
// older markup keeps working.
//
// The text-effect <select> is rendered by the shared TextTreatmentComponent
// (generic — no controller-specific data attributes), so we locate it by its
// data-live-preview-field and listen for its change event directly.
//
//   <div data-controller="text-effect-options"
//        data-text-effect-options-param-map-value='{"image_fill_drift":["intensity","fill_asset_id",...]}'>
//     <select data-live-preview-field="text_effect">…</select>
//     <div data-text-effect-options-target="fillOnly">…</div>       (fill_asset_id / fill_brightness)
//     <div data-text-effect-options-target="accentOnly">…</div>     (accent_color)
//     <div data-text-effect-options-target="highlightOnly">…</div>  (highlight_color)
//   </div>
export default class extends Controller {
  static targets = ["fillOnly", "accentOnly", "highlightOnly"]
  static values = { paramMap: Object }

  // Fallback preset → param sets, used only when no param-map is supplied.
  static FALLBACK = {
    fill_asset_id: ["image_fill_drift", "image_fill_breathe"],
    accent_color: ["keyword_accent", "gradient_sweep", "offset_print", "image_fill_drift", "image_fill_breathe"],
    highlight_color: ["marker_highlight"],
  }

  connect() {
    this.select = this.element.querySelector('[data-live-preview-field="text_effect"]')
    if (this.select) {
      this.onChange = () => this.toggle()
      this.select.addEventListener("change", this.onChange)
    }
    this.toggle()
  }

  disconnect() {
    if (this.select && this.onChange) {
      this.select.removeEventListener("change", this.onChange)
    }
  }

  toggle() {
    const effect = this.select ? this.select.value : ""
    this.apply(this.fillOnlyTargets, this.consumes(effect, "fill_asset_id") || this.consumes(effect, "fill_brightness"))
    this.apply(this.accentOnlyTargets, this.consumes(effect, "accent_color"))
    this.apply(this.highlightOnlyTargets, this.consumes(effect, "highlight_color"))
  }

  // Does `effect` consume `param`? Prefers the catalog-mirrored param map;
  // falls back to the hard-coded sets when the map is empty/absent.
  consumes(effect, param) {
    const map = this.paramMapValue
    if (map && Object.keys(map).length > 0) {
      const params = map[effect]
      return Array.isArray(params) && params.includes(param)
    }
    const fallback = this.constructor.FALLBACK[param] || []
    return fallback.includes(effect)
  }

  // Hide inactive blocks AND disable their inputs — a merely-hidden control is
  // still submitted by the browser, which would leak a stale value into a preset
  // that doesn't use it. Mirrors asset_source_controller.
  apply(targets, active) {
    targets.forEach((el) => {
      el.hidden = !active
      el.querySelectorAll("input, select, textarea").forEach((field) => {
        field.disabled = !active
      })
    })
  }
}
